<template>
  <Icon
    :id="`${name}-icon`"
    :name="`icons:${name}`"
    class="icon"
    mode="svg"
    :class="[sizeUnset ? 'icon--unset' : `icon--${size}`, shouldFlipInRtl ? 'rtl:-scale-x-100' : '']"
  />
</template>

<script setup lang="ts">
import { isDirectionalIcon } from '~/utils/i18n/icons';

const {
  name,
  size = 'md',
  sizeUnset = false,
  noFlip = false,
} = defineProps<{
  name: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  sizeUnset?: boolean;
  /**
   * Opts a directional icon (arrow/chevron/caret/back/next/prev/forward-left|right) out of the
   * automatic RTL mirror. Use this when the icon is rotated to point up/down (the flip would fight
   * the rotation) or when it lives inside a widget pinned to `dir="ltr"` (e.g. a carousel whose
   * track never mirrors) — Tailwind's `rtl:` variant matches on `[dir="rtl"] *` ancestry, so it
   * still fires even under a nested `dir="ltr"` container and must be suppressed explicitly.
   */
  noFlip?: boolean;
}>();

const shouldFlipInRtl = computed(() => !noFlip && isDirectionalIcon(name));
</script>

<style lang="scss">
.icon {
  display: inline-block;
  flex-shrink: 0;

  &--xs {
    width: 16px;
    height: 16px;
  }

  &--sm {
    width: 20px;
    height: 20px;
  }

  &--md {
    width: 24px;
    height: 24px;
  }

  &--lg {
    width: 32px;
    height: 32px;
  }

  &--xl {
    width: 48px;
    height: 48px;
  }

  &--unset {
    width: 100%;
    height: 100%;
  }
}
</style>
